import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { ListItem } from '../components/ui/ListItem.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useInfiniteContacts, useCategories, useBulkCategorize, useBulkDelete, useContactTagsList, useBulkTagContacts, useCreateContactTag, useCreateContactCategory, type Contact } from '../hooks/useContacts.js';
import { Modal } from '../components/ui/Modal.js';
import { ContactDetail } from '../components/contacts/ContactDetail.js';
import { ContactClaudePanel } from '../components/contacts/ContactClaudePanel.js';
import { ContactImportWizard } from '../components/contacts/ContactImportWizard.js';
import { CreateContactForm } from '../components/contacts/CreateContactForm.js';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { getContactCategoryTone as getCategoryTone } from '../lib/contactCategoryTone.js';

// ── Main page ─────────────────────────────────────────────────────────────────

export function ContactsPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [tag, setTag] = useState('');
  const [sortBy, setSortBy] = useState<'updated' | 'messaging'>('updated');
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [detailId, setDetailId] = useState<number | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  // Auto-select contact from ?selected=id (used by global search navigation)
  useEffect(() => {
    const sel = searchParams.get('selected');
    if (sel) {
      const parsed = parseInt(sel);
      if (!isNaN(parsed)) setDetailId(parsed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('selected')]);
  // SAB add-tag state
  const [tagCreateInput, setTagCreateInput] = useState('');
  const [tagCreateMode, setTagCreateMode] = useState(false);
  // SAB add-category state
  const [categoryCreateInput, setCategoryCreateInput] = useState('');
  const [categoryCreateMode, setCategoryCreateMode] = useState(false);
  useEffect(() => {
    setOverride({
      title: t('contacts.pageTitle'),
      heroIcon: 'contacts-large',
      heroIconColor: 'var(--evo-cyan)',
      bottomActions: (
        <PillGroup size="md">
          <Pill tone="cyan" icon="plus" onClick={() => setShowCreate(true)}>
            {t('contacts.create.title')}
          </Pill>
          <Pill tone="cyan" icon="contacts" onClick={() => setShowImport(true)}>
            {t('contacts.importCsv')}
          </Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: infiniteData, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteContacts({ search: debouncedSearch, category, source, tag, sortBy });
  const { data: categories } = useCategories();
  const { data: allTags = [] } = useContactTagsList();
  const bulkCategorize = useBulkCategorize();
  const bulkDelete = useBulkDelete();
  const bulkTag = useBulkTagContacts();
  const createTag = useCreateContactTag();
  const createCategory = useCreateContactCategory();

  const contacts: Contact[] = useMemo(
    () => infiniteData?.pages.flatMap(p => p.rows) ?? [],
    [infiniteData],
  );
  const totalContacts = infiniteData?.pages[0]?.total ?? 0;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useListKeyboardNav(contacts, detailId, id => setDetailId(id as number));

  const toggleCheck = useCallback((id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setChecked(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }, []);

  const handleBulkDelete = async () => {
    if (checked.size === 0) return;
    await bulkDelete.mutateAsync(Array.from(checked));
    setChecked(new Set());
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && checked.size > 0) setChecked(new Set());
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [checked.size]);

  return (
    <div className="flex flex-col h-full animate-fade-blur-in" style={{ background: 'var(--evo-bg)' }}>

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={v => setSearch(v)}
          onSubmit={() => setDebouncedSearch(search)}
          onClear={() => { setSearch(''); setDebouncedSearch(''); }}
          placeholder={t('common.search')}
        />
        <FilterRow.Dropdown<string>
          value={category}
          placeholder={t('contacts.allCategories')}
          options={[
            { value: '', label: t('contacts.allCategories') },
            ...(categories?.map(c => ({ value: c.category, label: t('categories.' + c.category, { defaultValue: c.category }) + ' (' + c.count + ')' })) ?? []),
          ]}
          onChange={val => setCategory(val)}
        />
        <FilterRow.Dropdown<string>
          value={source}
          placeholder={t('contacts.allSources', { defaultValue: 'All sources' })}
          options={[
            { value: '', label: t('contacts.allSources', { defaultValue: 'All sources' }) },
            { value: 'email', label: t('contacts.source_email', { defaultValue: 'Email' }) },
            { value: 'lead-engine', label: t('contacts.source_lead-engine', { defaultValue: 'Lead engine' }) },
            { value: 'manual', label: t('contacts.source_manual', { defaultValue: 'Manual' }) },
            { value: 'csv-import', label: t('contacts.source_csv-import', { defaultValue: 'CSV import' }) },
          ]}
          onChange={val => setSource(val)}
        />
        <FilterRow.Dropdown<string>
          value={tag}
          placeholder={t('contacts.allTags', { defaultValue: 'All tags' })}
          options={[
            { value: '', label: t('contacts.allTags', { defaultValue: 'All tags' }) },
            ...allTags.map(tg => ({ value: tg.slug, label: tg.label })),
          ]}
          onChange={val => setTag(val)}
        />
        <FilterRow.Dropdown<'updated' | 'messaging'>
          value={sortBy}
          placeholder={t('contacts.sortBy.updated', { defaultValue: 'Recently updated' })}
          options={[
            { value: 'updated', label: t('contacts.sortBy.updated', { defaultValue: 'Recently updated' }) },
            { value: 'messaging', label: t('contacts.sortBy.messaging', { defaultValue: 'Recent messaging activity' }) },
          ]}
          onChange={val => setSortBy(val)}
        />
      </FilterRow>

      <SelectionActionBar
        visible={checked.size > 0}
        countLabel={t('contacts.selected', { count: checked.size })}
        onClearSelection={() => { setChecked(new Set()); setTagCreateMode(false); setTagCreateInput(''); setCategoryCreateMode(false); setCategoryCreateInput(''); }}
      >
        {categoryCreateMode ? (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <div style={{ width: 140 }}>
              <Input
                autoFocus
                type="text"
                size="sm"
                fullWidth
                value={categoryCreateInput}
                onChange={v => setCategoryCreateInput(v)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && categoryCreateInput.trim()) {
                    const name = categoryCreateInput.trim();
                    createCategory.mutate(name, {
                      onSuccess: newCat => {
                        bulkCategorize.mutate({ ids: Array.from(checked), category: newCat.name }, {
                          onSuccess: () => setChecked(new Set()),
                        });
                        setCategoryCreateMode(false);
                        setCategoryCreateInput('');
                      },
                    });
                  }
                  if (e.key === 'Escape') { setCategoryCreateMode(false); setCategoryCreateInput(''); }
                }}
                placeholder={t('contacts.category.newPlaceholder', { defaultValue: 'New category name…' })}
              />
            </div>
            <Button
              variant="solid"
              tone="cyan"
              size="xs"
              disabled={!categoryCreateInput.trim()}
              onClick={() => {
                if (!categoryCreateInput.trim()) return;
                const name = categoryCreateInput.trim();
                createCategory.mutate(name, {
                  onSuccess: newCat => {
                    bulkCategorize.mutate({ ids: Array.from(checked), category: newCat.name }, {
                      onSuccess: () => setChecked(new Set()),
                    });
                    setCategoryCreateMode(false);
                    setCategoryCreateInput('');
                  },
                });
              }}
            >
              {t('common.create', { defaultValue: 'Create' })}
            </Button>
          </div>
        ) : (
          <Dropdown<string>
            value={null}
            placeholder={t('contacts.moveToCategory')}
            options={[
              ...(categories?.map(c => ({ value: c.category, label: t('categories.' + c.category, { defaultValue: c.category }) })) ?? []),
              { value: '__create__', label: `+ ${t('contacts.category.new', { defaultValue: 'New category' })}` },
            ]}
            onChange={val => {
              if (!val || checked.size === 0) return;
              if (val === '__create__') { setCategoryCreateMode(true); return; }
              bulkCategorize.mutate({ ids: Array.from(checked), category: val }, { onSuccess: () => setChecked(new Set()) });
            }}
            size="sm"
          />
        )}
        <SelectionActionBar.Divider />
        {/* Add tag */}
        {tagCreateMode ? (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <div style={{ width: 140 }}>
              <Input
                autoFocus
                type="text"
                size="sm"
                fullWidth
                value={tagCreateInput}
                onChange={v => setTagCreateInput(v)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && tagCreateInput.trim()) {
                    const slug = tagCreateInput.trim().toLowerCase().replace(/\s+/g, '_');
                    createTag.mutate({ slug, label: tagCreateInput.trim() }, {
                      onSuccess: newTag => {
                        bulkTag.mutate({ ids: Array.from(checked), tagId: newTag.id, action: 'add' });
                        setTagCreateMode(false);
                        setTagCreateInput('');
                      },
                    });
                  }
                  if (e.key === 'Escape') { setTagCreateMode(false); setTagCreateInput(''); }
                }}
                placeholder={t('contacts.tag.newPlaceholder', { defaultValue: 'New tag name…' })}
              />
            </div>
            <Button
              variant="solid"
              tone="cyan"
              size="xs"
              disabled={!tagCreateInput.trim()}
              onClick={() => {
                if (!tagCreateInput.trim()) return;
                const slug = tagCreateInput.trim().toLowerCase().replace(/\s+/g, '_');
                createTag.mutate({ slug, label: tagCreateInput.trim() }, {
                  onSuccess: newTag => {
                    bulkTag.mutate({ ids: Array.from(checked), tagId: newTag.id, action: 'add' });
                    setTagCreateMode(false);
                    setTagCreateInput('');
                  },
                });
              }}
            >
              {t('common.create', { defaultValue: 'Create' })}
            </Button>
          </div>
        ) : (
          <Dropdown<string>
            value={null}
            placeholder={t('contacts.addTag', { defaultValue: 'Add tag' })}
            options={[
              ...allTags.map(tg => ({ value: String(tg.id), label: tg.label })),
              { value: '__create__', label: `+ ${t('contacts.tag.new', { defaultValue: 'New tag' })}` },
            ]}
            onChange={val => {
              if (!val || checked.size === 0) return;
              if (val === '__create__') { setTagCreateMode(true); return; }
              bulkTag.mutate({ ids: Array.from(checked), tagId: parseInt(val), action: 'add' });
            }}
            size="sm"
          />
        )}
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('common.delete')}
          tone="danger"
          loading={bulkDelete.isPending}
          onClick={() => void handleBulkDelete()}
          confirmRequired
        />
      </SelectionActionBar>

      {/* 3-column body */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left: contact list (35%) */}
        <div className="flex flex-col shrink-0 overflow-hidden" style={{ width: 'clamp(300px, 33%, 480px)', borderRight: '1px solid var(--evo-stroke)' }}>

          {/* Header row */}
          <ListToolbar
            allSelected={contacts.length > 0 && contacts.every(c => checked.has(c.id))}
            someSelected={checked.size > 0 && !contacts.every(c => checked.has(c.id))}
            onSelectAll={sel => {
              if (sel) setChecked(new Set(contacts.map(c => c.id)));
              else setChecked(new Set());
            }}
            onExpandAll={() => setCollapsedBands(new Set())}
            onCollapseAll={() => setCollapsedBands(new Set(['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older']))}
            expandLabel={t('email.expandAll')}
            collapseLabel={t('email.collapseAll')}
          />

          {/* Contact list — infinite scroll with time bands */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="py-8 text-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</div>
            )}
            {!isLoading && contacts.length === 0 && (
              <div className="py-8 text-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.noContacts')}</div>
            )}
            {contacts.length > 0 && (
              <TimeBandedList<Contact>
                items={contacts}
                getTimestamp={(c) => c.updatedAt}
                getId={(c) => c.id}
                checkedIds={checked}
                onBandCheck={(ids, allChecked) => {
                  setChecked(prev => {
                    const next = new Set(prev);
                    if (allChecked) { ids.forEach(id => next.delete(id)); }
                    else { ids.forEach(id => next.add(id)); }
                    return next;
                  });
                }}
                collapsedBands={collapsedBands}
                onToggleBand={(band) => setCollapsedBands(prev => {
                  const next = new Set(prev);
                  next.has(band) ? next.delete(band) : next.add(band);
                  return next;
                })}
                renderItem={(contact) => {
                  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || '—';
                  const subtitle = contact.primaryEmail ?? contact.primaryPhone ?? undefined;
                  const tags = contact.tags ?? [];
                  return (
                    <div key={contact.id} data-list-item-id={contact.id}>
                      <ListItem
                        active={detailId === contact.id}
                        selected={checked.has(contact.id)}
                        onSelect={() => toggleCheck(contact.id)}
                        onClick={() => setDetailId(contact.id)}
                        title={name}
                        {...(subtitle !== undefined ? { subtitle } : {})}
                        badges={[
                          contact.source ? (
                            <Pill key="source" size="xs" tone="muted">{t(`contacts.source_${contact.source}`, { defaultValue: contact.source })}</Pill>
                          ) : null,
                          contact.category ? (
                            <Pill key="cat" size="xs" tone={getCategoryTone(contact.category)}>{t(`contacts.category.${contact.category}`, { defaultValue: contact.category })}</Pill>
                          ) : null,
                          ...tags.map(tag => (
                            <Pill key={`tag-${tag.id}`} size="xs" tone="muted">{tag.label}</Pill>
                          )),
                        ]}
                      />
                    </div>
                  );
                }}
                loadMoreSentinel={
                  <div ref={sentinelRef} style={{ height: 1 }}>
                    {isFetchingNextPage && <LoadingState size="sm" />}
                  </div>
                }
                emptyState={<EmptyState size="md" reserveSpace title={t('contacts.noContacts')} />}
              />
            )}
          </div>
        </div>

        {/* Center: contact detail (flex-1) */}
        <div className="flex flex-col overflow-hidden" style={{ flex: '1 1 0' }}>
          {detailId == null ? (
            <div className="flex-1 flex items-center justify-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>
              {t('contacts.selectContact')}
            </div>
          ) : (
            <ContactDetail
              id={detailId}
              onClose={() => setDetailId(null)}
              onDeleted={() => setDetailId(null)}
            />
          )}
        </div>

        {/* Right: Claude panel */}
        <ContactClaudePanel contact={contacts.find(c => c.id === detailId) ?? null} />

      </div>

      {/* New contact */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title={t('contacts.create.title')}
        size="sm"
      >
        <CreateContactForm
          onSuccess={c => { setDetailId(c.id); setShowCreate(false); }}
          onCancel={() => setShowCreate(false)}
        />
      </Modal>

      {/* Import wizard */}
      {showImport && <ContactImportWizard onClose={() => setShowImport(false)} />}
    </div>
  );
}
